import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@heidi/design-system/alert-dialog';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { toast } from '@heidi/design-system/toast';
import type { InAppHref, NavigatePort } from '@heidi/runtime/shell';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import type { ProjectDetail } from '../../../contracts/projects.types';
import { useBaseChatHref } from '../../../data/projects/useBaseChatHref';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import { useDeleteProject, useToggleSavedProject } from '../hooks';
import { ProjectChatMenu, ProjectChatMenuItems } from './ProjectChatMenu';

/**
 * What the app supplies to both header shapes: the Delete gate, the two chat-list
 * mutations (their cache writes are the app's chat-list keys) and the route home.
 */
export interface ProjectHeaderActionsHost {
  /** Whether Delete is offered at all, resolved from the app's flags. */
  canDelete: boolean;
  /** Pins or unpins the chat. Module-level, so its identity is stable. */
  useToggleSavedProject: (projectId: string) => { mutate: (isSaved: boolean) => void };
  /** Deletes the chat. Module-level, so its identity is stable. */
  useDeleteProject: () => {
    mutate: (
      projectId: string,
      callbacks: { onSuccess: () => void; onError: () => void }
    ) => void;
    isPending: boolean;
  };
  /** Where a deleted chat falls back to. */
  homeHref: InAppHref;
  onNavigate: NavigatePort;
}

type DeleteProjectHost = Pick<
  ProjectHeaderActionsHost,
  'useDeleteProject' | 'homeHref' | 'onNavigate'
>;

export interface ProjectHeaderActionsProps {
  project: ProjectDetail;
  projectId: string;
  onRename: () => void;
  showMakeAsWorkflow?: boolean;
  onMakeAsWorkflow?: () => void;
  readOnly?: boolean;
  showShare?: boolean;
  showDuplicate?: boolean;
  onShare?: () => void;
  onDuplicate?: () => void;
  onOpenHistory?: () => void;
  /** Session panel: open this chat on its full page. Omitted on the Work page. */
  onOpenFullscreen?: () => void;
}

/**
 * Confirms, then deletes the open chat and falls back to the chat home.
 * `ProjectChatMenu` only takes an `onDelete` callback; this dialog owns the
 * confirm flow, and the app supplies the mutation and the navigation.
 */
const DeleteProjectDialog = ({
  open,
  onOpenChange,
  projectId,
  useDeleteProject,
  homeHref,
  onNavigate,
}: DeleteProjectHost & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}) => {
  const intl = useIntl();
  const deleteProject = useDeleteProject();

  const handleConfirm = () => {
    deleteProject.mutate(projectId, {
      onSuccess: () => {
        onOpenChange(false);
        void onNavigate(homeHref);
      },
      onError: () => {
        toast({
          tone: 'destructive',
          title: intl.formatMessage({
            defaultMessage: 'Could not delete chat',
            id: 'AfKkL4gP6g',
            description: 'Toast shown when deleting a project chat fails',
          }),
        });
      },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {intl.formatMessage({
              defaultMessage: 'Delete this chat?',
              id: 'C9e3Nf883/',
              description: 'Delete chat confirm dialog: title',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {intl.formatMessage({
              defaultMessage: 'This action cannot be undone.',
              id: 'Ikupg3Ysuq',
              description: 'Delete chat confirm dialog: description',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteProject.isPending}>
            {intl.formatMessage({
              defaultMessage: 'Cancel',
              id: 'hVCGVDmTus',
              description: 'Delete chat confirm dialog: cancel button',
            })}
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={deleteProject.isPending}
            onClick={(event) => {
              event.preventDefault();
              handleConfirm();
            }}
          >
            {intl.formatMessage({
              defaultMessage: 'Delete',
              id: 'qOjz0lWDzo',
              description: 'Delete chat confirm dialog: confirm button',
            })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export interface ProjectHeaderCommonMenuItemsProps {
  onOpenHistory?: () => void;
  project: ProjectDetail;
  projectId: string;
  readOnly?: boolean;
  showShare?: boolean;
  showDuplicate?: boolean;
  onShare?: () => void;
  onDuplicate?: () => void;
}

/**
 * This component only renders menu items into a host's dropdown slot (the
 * routine-run header's `additionalMenuItems`) — it never owns the dropdown
 * itself, so it cannot rely on an external wrapper to keep the confirm
 * dialog mounted the way `ProjectHeaderActions` does for the non-routine
 * header. Selecting the Delete item calls `preventDefault()` so the host
 * dropdown stays open (and therefore stays mounted) while
 * `DeleteProjectDialog` — reused, not duplicated — runs its own confirm
 * flow; without that, the host's exit animation would unmount this
 * component (and the dialog with it) a couple hundred milliseconds after
 * opening it, regardless of user interaction.
 */
export const ProjectHeaderCommonMenuItemsView = ({
  project,
  projectId,
  readOnly = false,
  showShare = false,
  showDuplicate = false,
  onShare,
  onDuplicate,
  onOpenHistory,
  canDelete,
  useToggleSavedProject,
  useDeleteProject,
  homeHref,
  onNavigate,
}: ProjectHeaderCommonMenuItemsProps & ProjectHeaderActionsHost) => {
  const intl = useIntl();
  const toggleSaved = useToggleSavedProject(projectId);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <>
      <ProjectChatMenuItems
        agentRunId={project.agent_run_id}
        onOpenHistory={onOpenHistory}
        isSaved={project.is_saved}
        onToggleSaved={() => toggleSaved.mutate(!project.is_saved)}
        readOnly={readOnly}
        showShare={showShare}
        showDuplicate={showDuplicate}
        onShare={onShare}
        onDuplicate={onDuplicate}
      />
      {canDelete && !readOnly && (
        <>
          <DropdownMenuItem
            variant="destructive"
            onSelect={(event) => {
              event?.preventDefault();
              setIsDeleteDialogOpen(true);
            }}
          >
            <Trash2 aria-hidden />
            {intl.formatMessage({
              defaultMessage: 'Delete',
              id: 'p5c7fga9ot',
              description: 'Project chat overflow menu: delete the chat',
            })}
          </DropdownMenuItem>
          <DeleteProjectDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            projectId={projectId}
            useDeleteProject={useDeleteProject}
            homeHref={homeHref}
            onNavigate={onNavigate}
          />
        </>
      )}
    </>
  );
};

/**
 * Top-right action cluster for the project detail header: overflow menu
 * (rename, save/unsave, optional routine, copy run id). Kept ``shrink-0`` so the
 * title cluster on the left can truncate into the remaining width.
 */
export const ProjectHeaderActionsView = ({
  project,
  projectId,
  onRename,
  showMakeAsWorkflow = false,
  onMakeAsWorkflow,
  readOnly = false,
  showShare = false,
  showDuplicate = false,
  onShare,
  onDuplicate,
  onOpenHistory,
  onOpenFullscreen,
  canDelete,
  useToggleSavedProject,
  useDeleteProject,
  homeHref,
  onNavigate,
}: ProjectHeaderActionsProps & ProjectHeaderActionsHost) => {
  const toggleSaved = useToggleSavedProject(projectId);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  return (
    <div className="flex shrink-0 items-center gap-1">
      <ProjectChatMenu
        agentRunId={project.agent_run_id}
        onOpenHistory={onOpenHistory}
        onRename={onRename}
        isSaved={project.is_saved}
        onToggleSaved={() => toggleSaved.mutate(!project.is_saved)}
        showMakeAsWorkflow={showMakeAsWorkflow}
        onMakeAsWorkflow={onMakeAsWorkflow}
        onDelete={canDelete ? () => setIsDeleteDialogOpen(true) : undefined}
        readOnly={readOnly}
        showShare={showShare}
        showDuplicate={showDuplicate}
        onShare={onShare}
        onDuplicate={onDuplicate}
        onOpenFullscreen={onOpenFullscreen}
      />
      <DeleteProjectDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        projectId={projectId}
        useDeleteProject={useDeleteProject}
        homeHref={homeHref}
        onNavigate={onNavigate}
      />
    </div>
  );
};

const useProjectHeaderActionsHost = (): ProjectHeaderActionsHost => {
  const navigate = getWorkHostPort('shell').useNavigate();
  return {
    canDelete: getWorkHostPort('shell').useIsAgentHomeV1Enabled(),
    useToggleSavedProject,
    useDeleteProject,
    homeHref: useBaseChatHref(),
    onNavigate: navigate,
  };
};

/** The project detail header's action cluster, bound to the app. */
export const ProjectHeaderActions = (props: ProjectHeaderActionsProps) => {
  const host = useProjectHeaderActionsHost();
  return <ProjectHeaderActionsView {...props} {...host} />;
};

/** The shared chat menu items for the routine-run header, bound to the app. */
export const ProjectHeaderCommonMenuItems = (
  props: ProjectHeaderCommonMenuItemsProps
) => {
  const host = useProjectHeaderActionsHost();
  return <ProjectHeaderCommonMenuItemsView {...props} {...host} />;
};
